import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { ServiceSubscriptionSummary } from "@/api/service-subscription";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type CancellationMode = "period_end" | "immediate" | "blocked_by_checkout";

/** Mirrors the API's choice so the dialog explains what will actually happen (DES-CSB-016, §4.2). */
export function cancellationModeOf(subscription: ServiceSubscriptionSummary): CancellationMode {
  if (subscription.checkoutPending) return "blocked_by_checkout";
  return subscription.planKind === "paid" &&
    subscription.commercialState === "paid_active" &&
    subscription.billingSubscriptionSummary?.recurringBillingActive
    ? "period_end"
    : "immediate";
}

interface ServiceCancellationDialogProps {
  open: boolean;
  subscription: ServiceSubscriptionSummary;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  onChoosePlanChange?: () => void;
}

export function ServiceCancellationDialog({
  open,
  subscription,
  busy,
  onClose,
  onConfirm,
  onChoosePlanChange,
}: ServiceCancellationDialogProps) {
  const [typed, setTyped] = useState("");
  const [composing, setComposing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const mode = cancellationModeOf(subscription);
  const matchValue = subscription.kintoneSubdomain ?? "解約する";
  const matched = typed === matchValue && !composing;
  const hasScheduledChange = Boolean(
    subscription.scheduledGradeChange || subscription.billingSubscriptionSummary?.scheduledBillingCycleChange,
  );
  const lastDay = subscription.billingSubscriptionSummary?.expiresOn ?? null;

  useEffect(() => {
    if (!open) {
      setTyped("");
      return;
    }
    inputRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, busy, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="service-cancel-title"
        className="relative z-50 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border bg-card p-6 shadow-lg"
      >
        <h2 id="service-cancel-title" className="text-lg font-semibold">
          {mode === "period_end" ? "有料プランを期間末で解約します" : "サービス利用を解約します"}
        </h2>

        {mode === "blocked_by_checkout" ? (
          <p className="mt-3 text-sm">
            決済画面へ進んだお申込みが完了していません。決済を完了するか、決済画面で中止してから解約してください。
          </p>
        ) : (
          <>
            <p className="mt-3 text-sm">
              {mode === "period_end" && lastDay
                ? `${formatDay(lastDay)}まで今のプランを使えます。翌日からサービスが止まります。`
                : "解約するとすぐにサービスが止まります。"}
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>kintone のプラグインから添付ファイルを保存・表示できなくなります。</li>
              {subscription.planKind === "paid" ? <li>支払済みの期間の料金は返金されません。</li> : null}
              <li>接続先ストレージに保存済みのファイルは削除されず、そのまま残ります。</li>
              <li>公開リンクや接続環境など、サービスが用意した接続は解除されます。</li>
              <li>解約後にこの組織で利用を再開することはできません。{subscription.kintoneSubdomain ? `同じ kintone サブドメイン（${subscription.kintoneSubdomain}）で新しく登録することもできません。` : ""}</li>
              {hasScheduledChange ? <li>予約中のプラン変更は取り消されます。</li> : null}
              {subscription.commercialState === "payment_required" ? <li>未払いの請求は取り消されます。</li> : null}
            </ul>

            {mode === "period_end" ? (
              <div className="mt-4 rounded-md border bg-muted/30 p-3 text-sm">
                <p className="font-medium">解約の前に</p>
                <p className="mt-1 text-muted-foreground">
                  アプリ数の少ないプランや月払いに変えると、料金を抑えて使い続けられます。
                  解約予定は、最終利用日までならいつでも取り消せます。
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {onChoosePlanChange ? (
                    <Button type="button" variant="outline" size="sm" onClick={onChoosePlanChange} disabled={busy}>
                      プラン変更を検討する
                    </Button>
                  ) : null}
                  <Button type="button" variant="outline" size="sm" asChild>
                    <Link to="/contact">問い合わせる</Link>
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="mt-4 space-y-2">
              <Label htmlFor="service-cancel-confirm">確認のため「{matchValue}」と入力してください</Label>
              <Input
                id="service-cancel-confirm"
                ref={inputRef}
                value={typed}
                autoComplete="off"
                onChange={(event) => setTyped(event.target.value)}
                onCompositionStart={() => setComposing(true)}
                onCompositionEnd={() => setComposing(false)}
                placeholder={matchValue}
              />
            </div>
          </>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {mode === "blocked_by_checkout" ? "閉じる" : "解約しない"}
          </Button>
          {mode === "blocked_by_checkout" ? null : (
            <Button variant="destructive" onClick={() => void onConfirm()} disabled={!matched || busy}>
              {mode === "period_end" ? "期間末で解約する" : "解約する"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function formatDay(value: string) {
  return new Intl.DateTimeFormat("ja-JP", { dateStyle: "long", timeZone: "UTC" }).format(new Date(`${value}T00:00:00Z`));
}
